import dayjs from 'dayjs';
import { getFormattedName, getInitialsFromName } from '../../utility/general-utility.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { PhoneNumber } from './phone-number.js';
import { PhotoReference } from './file-reference/photo-reference.js';

export class PartnerAccount extends DataModel {
  static readonly TYPE_ID: string = 'ffd67fe8e7074857bb8e340966eef98d';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly formattedName: string;
  public readonly initials: string;

  constructor(
    public readonly uniqueId: string,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly email: string,
    public readonly phoneNumber: PhoneNumber,
    public readonly photo: PhotoReference | null = null,
    public readonly pushNotificationToken: string | null = null,
    public readonly creationDate: dayjs.Dayjs | null,
  ) {
    super();
    this.formattedName = getFormattedName(firstName, lastName)!;
    this.initials = getInitialsFromName(firstName, lastName)!;
  }

  get $typeId() {
    return PartnerAccount.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PartnerAccount.SCHEMA_VERSION);
  }

  get hasAName(): boolean {
    return (this.firstName !== null && this.firstName.length !== 0) ||
          (this.lastName !== null && this.lastName.length !== 0);
  }

  static fromMap(context: SerializationContext, map: SerializedData): PartnerAccount {
    return new PartnerAccount(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'firstName'),
        MapUtils.get<string>(map, 'lastName'),
        MapUtils.get<string>(map, 'email'),
        PhoneNumber.mustDecode(MapUtils.get<string>(map, 'phoneNumber')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getRaw(map, 'photo') as SerializedData),
        MapUtils.getOrNull<string>(map, 'pushNotificationToken'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'creationDate')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'firstName': this.firstName,
      'lastName': this.lastName,
      'email': this.email,
      'phoneNumber': this.phoneNumber.encode(),
      'photo': context.toDataOrNull(this.photo),
      'pushNotificationToken': this.pushNotificationToken,
      'creationDate': context.encodeDateOrNull(this.creationDate),
    };
  }

  clone(): this {
    return new PartnerAccount(
        this.uniqueId,
        this.firstName,
        this.lastName,
        this.email,
        this.phoneNumber.clone(),
        this.photo?.clone() ?? null,
        this.pushNotificationToken,
        TimeZone.cloneDateOrNull(this.creationDate),
    ) as this;
  }

  isSame(other: PartnerAccount): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.firstName === this.firstName &&
        other.lastName === this.lastName &&
        other.email === this.email &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        isSameData(other.photo, this.photo) &&
        other.pushNotificationToken === this.pushNotificationToken &&
        other.creationDate === this.creationDate;
  }

  copyWith(fields?: {
    firstName?: string,
    lastName?: string,
    email?: string,
    phoneNumber?: PhoneNumber,
    photo?: PhotoReference,
    creationDate?: dayjs.Dayjs
  }): PartnerAccount {
    return new PartnerAccount(
        this.uniqueId,
        fields?.firstName ?? this.firstName,
        fields?.lastName ?? this.lastName,
        fields?.email ?? this.email,
        fields?.phoneNumber ?? this.phoneNumber,
        fields?.photo ?? this.photo,
        this.pushNotificationToken,
        fields?.creationDate ?? TimeZone.cloneDateOrNull(this.creationDate),
    );
  }
}

export class PartnerAccountBuilder extends DataModelBuilder<PartnerAccount> {
  build(context: SerializationContext, map: SerializedData): PartnerAccount {
    return PartnerAccount.fromMap(context, map);
  }
}
